import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircleOutlined } from "@mui/icons-material";
import { tokens } from "../../../../theme";
import AnimatedTable, { ColumnDef } from "../AnimatedTable";
import apiService from "../../../../service/Api";
import type { IEquipment } from "../../../../types/equipment";
import { toast } from "react-toastify";

const ROWS_PER_PAGE = 10;

interface MaintenanceApiItem {
  id: number;
  maintenance_date?: string | null;
  performed_by?: string | null;
  notes?: string | null;
  cost?: string | null;
  next_due_date?: string | null;
  equipment?: number | null;
  issue?: number | string | null;
  status?: "pending" | "completed" | "cancelled";
}

interface MaintenanceRow {
  id: number;
  issue: string;
  date: string;
  status: "pending" | "completed" | "cancelled";
  technician: string;
  cost: string;
}

interface Props {
  equipment: IEquipment;
  refreshKey?: number;
}

export default function MaintenanceTab({ equipment, refreshKey }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [maintenance, setMaintenance] = useState<MaintenanceRow[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMoney = (value?: string | null) => {
    if (!value) return "—";
    return `$${Number(value).toFixed(2)}`;
  };

  const fetchMaintenance = useCallback(async () => {
    if (!equipment.id) return;
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get<{
        count: number;
        results: MaintenanceApiItem[];
      }>(
        `/api/equipments/maintenance/?equipment=${equipment.id}&page=${page + 1}&page_size=${ROWS_PER_PAGE}`,
      );

      const rows: MaintenanceRow[] = response.data.results.map((item) => ({
        id: item.id,
        issue:
          item.notes ||
          (item.issue ? `Issue #${item.issue}` : "General Maintenance"),
        date: formatDate(item.maintenance_date),
        status: item.status || "pending",
        technician: item.performed_by || "—",
        cost: formatMoney(item.cost),
      }));

      setMaintenance(rows);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error(err);
      setMaintenance([]);
      setTotalCount(0);
      setError("Failed to load maintenance records.");
    } finally {
      setLoading(false);
    }
  }, [equipment.id, page]);

  useEffect(() => {
    fetchMaintenance();
  }, [fetchMaintenance]);

  useEffect(() => {
    if (refreshKey === undefined) return;
    fetchMaintenance();
  }, [refreshKey, fetchMaintenance]);

  const handleMarkComplete = async (id: number) => {
    try {
      setCompleting(id);
      await apiService.patch(`/api/equipments/maintenance/${id}/`, {
        status: "completed",
      });
      toast.success("Maintenance marked as complete.");
      await fetchMaintenance();
    } catch {
      toast.error("Failed to mark maintenance as complete.");
    } finally {
      setCompleting(null);
    }
  };

  const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE) || 1;

  const statusStyles: Record<
    MaintenanceRow["status"],
    { bg: string; color: string; label: string }
  > = {
    pending:   { bg: "#fff3e0", color: "#e65100", label: "Pending" },
    completed: { bg: "#e8f5e9", color: "#2e7d32", label: "Completed" },
    cancelled: { bg: "#fafafa", color: "#9e9e9e", label: "Cancelled" },
  };

  const columns: ColumnDef<MaintenanceRow>[] = [
    {
      key: "issue",
      label: "Issue / Notes",
      cellSx: { fontWeight: 500, color: colors.primary[700] },
      render: (row) => row.issue,
    },
    { key: "date", label: "Date", render: (row) => row.date },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const style = statusStyles[row.status];
        return (
          <Chip
            label={style.label}
            size="small"
            sx={{
              bgcolor: style.bg,
              color: style.color,
              fontWeight: 600,
              borderRadius: "16px",
            }}
          />
        );
      },
    },
    { key: "technician", label: "Technician", render: (row) => row.technician },
    { key: "cost", label: "Cost", render: (row) => row.cost },
    {
      key: "action",
      label: "",
      align: "center",
      render: (row) => {
        if (row.status !== "pending") return null;
        return (
          <Tooltip title="Mark Complete" arrow>
            <IconButton
              size="small"
              disabled={completing === row.id}
              onClick={() => handleMarkComplete(row.id)}
            >
              {completing === row.id ? (
                <CircularProgress size={16} />
              ) : (
                <CheckCircleOutlined
                  sx={{ fontSize: 18, color: "#2e7d32" }}
                />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        p: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          color={theme.palette.text.primary}
        >
          Maintenance Records
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AnimatedTable
          columns={columns}
          rows={maintenance}
          getRowId={(row) => row.id}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No maintenance records found."
        />
      )}
    </Box>
  );
}